import { Injectable } from '@angular/core'

import { Subscription } from 'rxjs'

/** The two lifetimes a turn and a run share. No rendered state: a new turn retires requests,
 * while only leaving the conversation drops the run subscription and clears execution-local state. */
@Injectable({ providedIn: 'root' })
export class HarnessSessionLifetime {
  // Bumped by every new turn, recovery, cancel and clear: a request started under an older
  // value is retired, so a recovery that lands after the maker moved on must not write into
  // the conversation they are looking at now.
  #requestGeneration = 0

  // Bumped only when the conversation on screen changes, so a running plan survives a turn
  // sent beside it (which bumps `requestGeneration`) and only a switch of conversation
  // retires it.
  #conversationGeneration = 0

  // A turn is admitted while a plan runs (D14), so the run has its own slot here; the session
  // still owns the turn subscription. Neither subscription is rendered state.
  #runStream: Subscription | null = null
  #resetExecution: (() => void) | null = null

  /** A check that stays true until the next request retires the ones in flight. */
  latestRequestCheck(): () => boolean {
    const generation = this.#requestGeneration
    return () => generation === this.#requestGeneration
  }

  /** A check that stays true until the conversation on screen changes. */
  sameConversationCheck(): () => boolean {
    const generation = this.#conversationGeneration
    return () => generation === this.#conversationGeneration
  }

  invalidateRequestChecks(): void {
    this.#requestGeneration += 1
  }

  /** Clear execution-local state whenever the conversation is left. Returns the call that takes
   * the reset off again, for the store that registered it to make when it is destroyed. */
  registerExecutionReset(reset: () => void): () => void {
    this.#resetExecution = reset

    return () => {
      if (this.#resetExecution === reset) {
        this.#resetExecution = null
      }
    }
  }

  leaveConversation(): void {
    this.dropRunStream()
    this.invalidateRequestChecks()
    this.#conversationGeneration += 1
    this.#resetExecution?.()
  }

  replaceRunStream(stream: Subscription): void {
    this.dropRunStream()
    this.#runStream = stream
  }

  dropRunStream(): void {
    this.#runStream?.unsubscribe()
    this.#runStream = null
  }

  isRunStreamOpen(): boolean {
    return !!this.#runStream && !this.#runStream.closed
  }
}
